import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { WA_TUTORING_SUBURBS } from '@/data/waTutoringSuburbs';
import { MapPin, ArrowRight, BookOpen, Phone, GraduationCap, Award, Calculator, PenTool } from 'lucide-react';

const SITE_URL = 'https://schoolola.com.au';

const REGION_ORDER: Record<string, number> = {
  'Perth Western Suburbs': 1,
  'Perth Inner North': 2,
  'Perth Inner West': 3,
  'Perth Northern Growth Corridor': 4,
  'Perth Far North': 5,
  'Perth Eastern Growth Corridor': 6,
  'Perth South of River': 7,
  'Perth South-East': 8,
  'Perth Southern Growth Corridor': 9,
};

function groupByRegion(suburbs: typeof WA_TUTORING_SUBURBS) {
  const groups: Record<string, typeof WA_TUTORING_SUBURBS> = {};
  for (const s of suburbs) {
    if (!groups[s.region]) groups[s.region] = [];
    groups[s.region].push(s);
  }
  const sortedRegions = Object.keys(groups).sort(
    (a, b) => (REGION_ORDER[a] || 99) - (REGION_ORDER[b] || 99)
  );
  return sortedRegions.map((r) => ({ region: r, suburbs: groups[r] }));
}

const programs = [
  { title: 'Maths tutoring WA', icon: Calculator, desc: 'Year 1-10 maths aligned to the WA curriculum, from foundations to ATAR readiness.' },
  { title: 'English tutoring WA', icon: PenTool, desc: 'Reading comprehension, writing, grammar and vocabulary with weekly feedback.' },
  { title: 'PEAC preparation WA', icon: Award, desc: 'Targeted PEAC test coaching for reasoning, problem solving and test-day strategy.' },
  { title: 'NAPLAN tutoring WA', icon: BookOpen, desc: 'Year 3, 5, 7 and 9 NAPLAN coaching: numeracy, reading, writing and language.' },
];

export default function TutoringWAIndex() {
  const { setSelectedState } = useLocation();

  useEffect(() => {
    setSelectedState('WA');
    localStorage.setItem('schoolola_selected_state', 'WA');
  }, [setSelectedState]);

  const title = 'Tutoring Western Australia - Maths, English, PEAC & NAPLAN | Schoolola';
  const description =
    'Find tutoring across Western Australia. Schoolola offers maths, English, PEAC preparation and NAPLAN coaching in Subiaco, Joondalup, Ellenbrook, Scarborough and 15 more WA suburbs.';
  const canonical = `${SITE_URL}/tutoring/western-australia`;

  const grouped = groupByRegion(WA_TUTORING_SUBURBS);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={[
          'tutoring western australia', 'wa tutoring', 'tutoring perth', 'perth tutoring',
          'maths tutoring wa', 'english tutoring wa', 'PEAC preparation wa', 'NAPLAN tutoring wa',
          'tutoring subiaco', 'tutoring joondalup', 'tutoring ellenbrook', 'tutoring scarborough',
          'tutoring morley', 'tutoring cottesloe', 'tutoring applecross', 'tutoring baldivis',
          'online tutoring western australia', 'homework help perth', 'private tutor perth',
        ].join(', ')} />

        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />

        <meta name="geo.region" content="AU-WA" />
        <meta name="geo.placename" content="Western Australia" />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Tutoring locations in Western Australia',
            itemListElement: WA_TUTORING_SUBURBS.map((s, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: `Tutoring in ${s.name}, WA`,
              url: `${SITE_URL}/tutoring/${s.slug}`,
            })),
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Tutoring Western Australia', item: canonical },
            ],
          })}
        </script>
      </Helmet>

      <Header />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="relative py-16 md:py-24 px-6 bg-gradient-to-br from-primary/10 via-background to-accent/5">
          <div className="container mx-auto max-w-6xl text-center space-y-6">
            <div className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1 rounded-full bg-primary/10 text-primary">
              <MapPin className="h-4 w-4" /> Western Australia
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight">
              Tutoring across Western Australia
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Expert maths, English, PEAC preparation and NAPLAN coaching for WA families.
              Browse our tutoring locations by region and find the programme nearest you.
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <Button asChild size="lg">
                <a href="#locations">Find your suburb</a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/programs">Browse programmes</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Programmes overview */}
        <section className="py-16 px-6">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              What we tutor in WA
            </h2>
            <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
              Structured weekly programmes built around the WA curriculum, with small groups and clear progress tracking from foundation skills to selective pathways.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {programs.map((p, i) => {
                const Icon = p.icon;
                return (
                  <Card key={i} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/20">
                    <CardContent className="p-6">
                      <Icon className="h-10 w-10 text-primary mb-4 group-hover:scale-110 transition-transform" />
                      <h3 className="text-lg font-semibold mb-2">{p.title}</h3>
                      <p className="text-sm text-muted-foreground">{p.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Suburb directory */}
        <section id="locations" className="py-16 px-6 bg-muted/40">
          <div className="container mx-auto max-w-6xl space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold">Tutoring locations in Western Australia</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Select your suburb to learn more about tutoring services, local schools supported, nearby areas covered and how to book a free consultation.
              </p>
            </div>

            {grouped.map(({ region, suburbs }) => (
              <div key={region}>
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  {region}
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {suburbs.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/tutoring/${s.slug}`}
                      className="group"
                      aria-label={`Explore tutoring in ${s.name} - maths, English, PEAC and NAPLAN`}
                    >
                      <Card className="h-full hover:shadow-md transition-all duration-300 border-2 hover:border-primary/20">
                        <CardContent className="p-5 flex flex-col h-full">
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="text-lg font-semibold group-hover:text-primary transition-colors">
                              Tutoring in {s.name}
                            </h4>
                            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                          </div>
                          <p className="text-sm text-muted-foreground mb-3 flex-1">{s.blurb}</p>
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                              <GraduationCap className="h-3 w-3" /> {s.parentIntent}
                            </span>
                          </div>
                          <div className="mt-3 pt-3 border-t border-border">
                            <p className="text-xs text-muted-foreground">
                              Also serving: {s.nearby.map((n) => n.name).join(', ')}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why Schoolola in WA */}
        <section className="py-16 px-6">
          <div className="container mx-auto max-w-6xl grid md:grid-cols-2 gap-10 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">Why families across WA choose Schoolola</h2>
              <ul className="space-y-4">
                {[
                  'WA curriculum aligned - every lesson mapped to what your child learns at school',
                  'PEAC and GATE specialists with proven track records in selective entry',
                  'Small-group tutoring with individual attention and weekly progress feedback',
                  'Flexible in-centre and online options for Perth metro and regional families',
                  'NAPLAN coaching that builds real skills, not just test tricks',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Award className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Button asChild size="lg">
                <Link to="/our-story">Learn more about Schoolola</Link>
              </Button>
            </div>
            <div className="space-y-4">
              <Card>
                <CardContent className="p-6 space-y-3">
                  <p className="text-lg font-medium italic text-foreground">
                    "The GATE preparation programme helped our son gain entry into the selective programme. Highly recommend their tutoring!"
                  </p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>Jennifer L., Perth, WA</span>
                  </div>
                </CardContent>
              </Card>
              <div className="grid grid-cols-1 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-primary">20+</div>
                    <div className="text-sm text-muted-foreground">WA suburbs served</div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-6 bg-primary/5">
          <div className="container mx-auto max-w-3xl text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold">Not sure which suburb or programme?</h2>
            <p className="text-muted-foreground">
              Tell us about your child and we will recommend the right tutoring programme - free, no obligation.
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <Button asChild size="lg">
                <Link to="/contact">Request a free consultation</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/book-assessment">Book an assessment</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
